import DownloadIcon from '@mui/icons-material/Download';
import LocalPrintshopIcon from '@mui/icons-material/LocalPrintshop';
import { Link, Stack, Tooltip, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import DialogContent from '@mui/material/DialogContent';
import Grid from '@mui/material/Grid';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { CSVLink } from 'react-csv';
import { useDispatch } from 'react-redux';
import { PopupComponent } from 'src/components/common/PopupComponent';
import { getPoratlHost } from 'src/components/hooks/useUtil';
import { archLevel } from 'src/data/config';
import { sendRequest } from 'src/hooks/useFetch';
import { setMessage } from 'src/store/Message';
import { useAppSelector } from 'src/store/hooks';
import { usePreferedDateFormat } from 'src/util/DateFormatter';

interface IVersionHistoryProps {
  setAnchorEl?: () => void;
  openVersoinHistory: boolean;
  setOpenVersoinHistory: (isOpen: boolean) => void;
  documentId: string;
  title: string;
  auditModuleSection: string;
}

const VersionHistory = (vhProps: IVersionHistoryProps) => {
  const dispatch = useDispatch();
  const [versionHistoryDetails, setVersionHistoryDetails] = useState<any>([]);
  const [pageSize, setPageSize] = React.useState<number>(5);
  const studyId: any = useAppSelector(state => state.site.selectedStudy);
  const selectedCountry = useAppSelector(state => state.site.selectedCountry);
  const selectedSite = useAppSelector(state => state.site.selectedSite);
  const { preferedDateFormat, userPreferedDateFormat } = usePreferedDateFormat();
  const [loading, setLoading] = useState<any>(false);
  const [latestVersion, setLatestVersion] = useState(0);
  const [csvDataDownload, setCsvDataDownload] = useState<any>([]);
  const downloadDateFormat = userPreferedDateFormat.includes('/')
    ? userPreferedDateFormat.replaceAll('/', '_')
    : userPreferedDateFormat.replaceAll('-', '_');

  const versionHistoryApi = archLevel === 'site' ? 'get_isf_document_version_history' : 'get_document_version_history';
  const versionBasedDetailApi =
    archLevel === 'site'
      ? 'get_isf_documents_by_studyid_tmf_document_id_tmf_doc_version_number'
      : 'get_documents_by_studyid_tmf_document_id_tmf_doc_version_number';

  const getVersionHistory = async () => {
    try {
      setLoading(true);
      const options: any = {
        endPointEnv: archLevel === 'site' ? 'isf' : 'etmf',
        pathVariables: {
          study_id: studyId,
          country: selectedCountry,
          site_id: selectedSite,
          appName: process.env.REACT_APP_NAME,
          tmfDocId: vhProps.documentId,
          audit_module_section: vhProps.auditModuleSection
        }
      };
      const result: any = await sendRequest(versionHistoryApi, options);
      const updated = result.map((res: any) => {
        return { id: res.document_version.document_id, fileId: res.file_master_id, ...res.document_version };
      });
      setLatestVersion(Math.max(...result.map((o: any) => o.document_version.isf_doc_version_number)));
      const csvData = result.map((res: any) => {
        return {
          ...res.document_version,
          [`${archLevel === 'site' ? 'isf_doc_upload_date' : 'tmf_doc_upload_date'}`]: moment(
            new Date(
              archLevel === 'site' ? res.document_version.isf_doc_upload_date : res.document_version.tmf_doc_upload_date
            )
          )
            .format(`${userPreferedDateFormat.toUpperCase()}, hh:mm:ss A`)
            .toString()
        };
      });
      setVersionHistoryDetails(updated);
      setCsvDataDownload(csvData);
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      setVersionHistoryDetails([]);
      if (error && error?.response?.status === 403) {
        dispatch(setMessage({ msgDetails: 'Error 403 Restricted User permission', severity: 'error' }));
      } else if (error && error.response?.status === 401) {
        dispatch(setMessage({ msgDetails: 'Error 401 Unauthorized access', severity: 'error' }));
      }
    }
  };
  useEffect(() => {
    getVersionHistory();
  }, []);

  const getVesionBasedDocumentDetails = async (vd: any) => {
    try {
      const options = {
        endPointEnv: archLevel === 'site' ? 'isf' : 'etmf',
        study_id: studyId,
        pathVariables: {
          site_id: selectedSite,
          country: selectedCountry,
          tmfDocumentId: archLevel === 'site' ? vd.isf_document_id : vd.tmf_document_id,
          docVersionNumber: archLevel === 'site' ? vd.isf_doc_version_number : vd.tmf_doc_version_number,
          documentId: vd.document_id,
          appName: process.env.REACT_APP_NAME,
          latest_version_hyperlink: !(archLevel === 'site'
            ? vd.isf_doc_version_number
            : vd.tmf_doc_version_number < latestVersion.toFixed(1)),
          document_id: vd.document_id
        }
      };
      const result: any = await sendRequest(versionBasedDetailApi, options);
      window.parent.postMessage(
        {
          action: 'PUSH_APP',
          slug_id: archLevel === 'site' ? process.env.REACT_APP_ISF_SLUG : process.env.REACT_APP_ETMF_SLUG,
          viewerDocument: {
            document_id: result.document_data.id,
            file_master_id: result.file_master.id,
            study_id: studyId,
            site_id: selectedSite,
            country: selectedCountry
          }
        },
        getPoratlHost()
      );
      if (vhProps.openVersoinHistory) {
        vhProps.setOpenVersoinHistory(false);
      }
    } catch (error: any) {
      setLoading(false);
      if (error && error?.response?.status === 403) {
        dispatch(setMessage({ msgDetails: 'Error 403 Restricted User permission', severity: 'error' }));
      } else if (error && error.response?.status === 401) {
        dispatch(setMessage({ msgDetails: 'Error 401 Unauthorized access', severity: 'error' }));
      } else {
        dispatch(setMessage({ msgDetails: 'Error while getting document data', severity: 'error' }));
      }
    }
  };

  const gridFunc = (versionDetails: any) => {
    getVesionBasedDocumentDetails(versionDetails);
    // handleClose();
    // setDocumentToDisplay1(versionDetails);
    // setDataGridComponent(false);
  };

  const columns: GridColDef[] = [
    {
      field: archLevel === 'site' ? 'isf_doc_version_number' : 'tmf_doc_version_number',
      headerName: 'Version',
      minWidth: 230,
      flex: 1,
      renderCell: (params: any) => (
        <Link onClick={() => gridFunc(params.row)}>{params.value?.toFixed(1).toString() || ''}</Link>
      )
    },
    {
      field: archLevel === 'site' ? 'isf_doc_last_modified_by' : 'tmf_doc_last_modified_by',
      headerName: 'Name',
      minWidth: 230,
      flex: 1
    },
    {
      field: archLevel === 'site' ? 'isf_doc_upload_date' : 'tmf_doc_upload_date',
      headerName: 'Date Modified',
      minWidth: 180,
      flex: 1,
      valueFormatter: (params: any) => {
        return preferedDateFormat(params.value?.split('T')[0]);
      }
    }
  ];

  const handleClose = () => {
    vhProps.setOpenVersoinHistory(false);
  };

  function TabPanel(props: { [x: string]: any; children: any; value: any; index: any }) {
    const { children, value, index, ...other } = props;

    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`vertical-tabpanel-${index}`}
        aria-labelledby={`vertical-tab-${index}`}
        {...other}
      >
        {value === index && (
          <Box sx={{ p: 3 }}>
            <Typography>{children}</Typography>
          </Box>
        )}
      </div>
    );
  }
  const headersForCSV = [
    { label: 'Version', key: archLevel === 'site' ? 'isf_doc_version_number' : 'tmf_doc_version_number' },
    { label: 'Name', key: archLevel === 'site' ? 'isf_doc_last_modified_by' : 'tmf_doc_last_modified_by' },
    { label: 'Date Modified', key: archLevel === 'site' ? 'isf_doc_upload_date' : 'tmf_doc_upload_date' }
  ];
  const handlePrint = () => {
    setTimeout(() => {
      window.print();
    }, 300);
  };

  TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired
  };

  return (
    <div>
      <div className="query-modal">
        <PopupComponent aria-labelledby="customized-dialog-title" open={vhProps.openVersoinHistory} maxWidth="lg">
          <DialogContent dividers className="audit-history-modal" sx={{ p: 2, width: '800px' }}>
            <div className="audit-card">
              <Grid container spacing={0}>
                <Grid item md={12}>
                  <Box sx={{ bgcolor: 'background.paper', pt: 0 }}>
                    <Grid container mb={1}>
                      <Grid item md={10}>
                        <h2>Version History - {vhProps.title}</h2>
                      </Grid>
                      <Grid
                        item
                        md={2}
                        display="flex"
                        justifyContent="flex-end"
                        className="print-actions"
                        id="no-print"
                        alignItems="flex-start"
                      >
                        <Tooltip placement="top" title="Print" arrow>
                          <Button onClick={e => handlePrint()} variant="text" className="grey-btn">
                            <LocalPrintshopIcon />
                          </Button>
                        </Tooltip>
                        <Tooltip placement="top" title="Download as CSV" arrow>
                          <Button variant="text" className="grey-btn" sx={{ ml: 2 }}>
                            <CSVLink
                              headers={headersForCSV}
                              data={csvDataDownload}
                              filename={`VersionHistory_${vhProps.auditModuleSection}_${moment(new Date()).format(
                                `${downloadDateFormat.toUpperCase()} HH:mm:ss`
                              )}.csv`}
                            >
                              <DownloadIcon className="grey-btn" />
                            </CSVLink>
                          </Button>
                        </Tooltip>
                      </Grid>
                    </Grid>
                    <Grid container>
                      <Grid item md={12}>
                        <div style={{ height: 360, width: '100%', minWidth: '760px' }}>
                          <DataGrid
                            rows={versionHistoryDetails}
                            columns={columns}
                            pageSize={pageSize}
                            onPageSizeChange={newPageSize => setPageSize(newPageSize)}
                            rowsPerPageOptions={[5, 10, 20]}
                            pagination
                            disableSelectionOnClick
                            initialState={{
                              sorting: {
                                sortModel: [{ field: 'updated_on', sort: 'desc' }]
                              }
                            }}
                            loading={loading}
                          />
                        </div>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
                <Grid item md={12} id="no-print" mt={2}>
                  <Grid container spacing={2}>
                    <Grid item md={12} display="flex" justifyContent="flex-end">
                      <Stack direction="row" spacing={2}>
                        <Button
                          variant="text"
                          href="#contained-buttons"
                          className="grey-btn"
                          onClick={handleClose}
                          disabled={loading}
                        >
                          Close
                        </Button>
                      </Stack>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </div>
          </DialogContent>
        </PopupComponent>
      </div>
    </div>
  );
};

export default VersionHistory;
